
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface CharacterColorTheme {
  id: string;
  name: string;
  color_hex?: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export const useCharacterColorThemes = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: colorThemes = [], isLoading, error } = useQuery({
    queryKey: ['character-color-themes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('character_color_themes')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as CharacterColorTheme[];
    },
  });

  const createColorTheme = useMutation({
    mutationFn: async ({ name, color_hex }: { name: string; color_hex?: string }) => {
      const maxOrder = Math.max(...colorThemes.map(ct => ct.display_order), -1);
      const { data, error } = await supabase
        .from('character_color_themes')
        .insert([{ 
          name, 
          color_hex,
          display_order: maxOrder + 1,
          is_active: true 
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['character-color-themes'] });
      toast({
        title: "Success",
        description: "Color theme created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create color theme",
        variant: "destructive",
      });
    },
  });

  const updateColorTheme = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<CharacterColorTheme> }) => {
      const { data, error } = await supabase
        .from('character_color_themes')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['character-color-themes'] });
      toast({
        title: "Success",
        description: "Color theme updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update color theme",
        variant: "destructive",
      });
    },
  });

  const deleteColorTheme = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('character_color_themes')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['character-color-themes'] });
      toast({
        title: "Success",
        description: "Color theme deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete color theme",
        variant: "destructive",
      });
    },
  });

  return {
    colorThemes,
    isLoading,
    error,
    createColorTheme,
    updateColorTheme,
    deleteColorTheme,
  };
};
